// ============================================================================
// HUSO HORARIO DEL PROYECTO — CATÁLOGO Y FUENTE ÚNICA
// ============================================================================
// Espejo de localeConfig.js, para el otro eje de la configuración regional:
//
//   locale   → CÓMO SE VE   (separador decimal, nombres de mes, orden de fecha)
//   timezone → QUÉ SIGNIFICA (en qué huso se interpreta un instante)
//
// La diferencia importa al migrar: el locale es presentación pura — cambiarlo
// no puede corromper un dato. La zona horaria es interpretación: el mismo
// instante mostrado en el huso equivocado es un dato INCORRECTO, no sólo mal
// formateado.
//
// Igual que localeConfig, este módulo es una HOJA: no importa nada de la app,
// para que puedan consumirlo tanto auth.js como chartTime.js sin ciclos.
//
// DEBE COINCIDIR con PROJECT_TIMEZONES de api/helpers/projectConfig.js.
//
// >>> POR QUÉ IANA Y NO UN OFFSET <<<
// El modelo anterior guardaba `zona_horaria_utc` (un entero). Eso obligaba a
// tener dos entradas de catálogo distintas, "Chile (Invierno) GMT-4" y "Chile
// (Verano) GMT-3", y a que alguien editara cada proyecto dos veces al año.
// `America/Santiago` describe las REGLAS del huso, no un offset: la librería
// resuelve el desplazamiento correcto para cada instante, incluido el cambio
// de hora.

export const SUPPORTED_TIMEZONES = [
  { id: 'UTC',                            label: 'UTC', grupo: 'Base' },
  { id: 'America/Santiago',               label: 'Chile continental', grupo: 'Chile' },
  { id: 'America/Punta_Arenas',           label: 'Chile · Magallanes', grupo: 'Chile' },
  { id: 'Pacific/Easter',                 label: 'Chile · Isla de Pascua', grupo: 'Chile' },
  { id: 'America/Argentina/Buenos_Aires', label: 'Argentina', grupo: 'América' },
  { id: 'America/Sao_Paulo',              label: 'Brasil (São Paulo)', grupo: 'América' },
  { id: 'America/La_Paz',                 label: 'Bolivia', grupo: 'América' },
  { id: 'America/Caracas',                label: 'Venezuela', grupo: 'América' },
  { id: 'America/Lima',                   label: 'Perú', grupo: 'América' },
  { id: 'America/Bogota',                 label: 'Colombia', grupo: 'América' },
  { id: 'America/Guayaquil',              label: 'Ecuador', grupo: 'América' },
  { id: 'America/Mexico_City',            label: 'México', grupo: 'América' },
  { id: 'America/Guatemala',              label: 'Centroamérica', grupo: 'América' },
  { id: 'America/Denver',                 label: 'EE.UU. · Mountain Time', grupo: 'América' },
  { id: 'America/Los_Angeles',            label: 'EE.UU. · Pacific Time', grupo: 'América' },
  { id: 'Europe/Madrid',                  label: 'España', grupo: 'Europa' },
  { id: 'Europe/Paris',                   label: 'Francia / Alemania', grupo: 'Europa' },
  { id: 'Europe/Moscow',                  label: 'Moscú', grupo: 'Europa' },
  { id: 'Europe/Istanbul',                label: 'Turquía', grupo: 'Europa' },
  { id: 'Asia/Shanghai',                  label: 'China', grupo: 'Asia / Oceanía' },
  { id: 'Asia/Singapore',                 label: 'Singapur', grupo: 'Asia / Oceanía' },
  { id: 'Asia/Tokyo',                     label: 'Japón', grupo: 'Asia / Oceanía' },
  { id: 'Asia/Seoul',                     label: 'Corea del Sur', grupo: 'Asia / Oceanía' },
  { id: 'Australia/Sydney',               label: 'Sydney / Melbourne', grupo: 'Asia / Oceanía' },
  { id: 'Pacific/Auckland',               label: 'Nueva Zelanda', grupo: 'Asia / Oceanía' }
];

export const SUPPORTED_TIMEZONE_IDS = SUPPORTED_TIMEZONES.map((z) => z.id);

/** Huso por defecto cuando el proyecto no tiene ninguno configurado. */
export const DEFAULT_TIMEZONE = 'America/Santiago';

/** ¿Es un identificador del catálogo? */
export function isSupportedTimezone(value) {
  return SUPPORTED_TIMEZONE_IDS.includes(String(value || '').trim());
}

/**
 * Normaliza un identificador de huso.
 * Devuelve el DEFAULT ante un valor desconocido — nunca un huso inventado, que
 * haría que la app interprete instantes en una zona inexistente.
 */
export function resolveTimezone(value) {
  const normalizado = String(value || '').trim();
  return isSupportedTimezone(normalizado) ? normalizado : DEFAULT_TIMEZONE;
}

/** Opciones para el selector de Administración > Proyectos. */
export function timezoneOptions() {
  return SUPPORTED_TIMEZONES.map(({ id }) => ({ value: id, text: describirTimezone(id) }));
}

// (Se eliminaron LEGACY_POR_INDICE, LEGACY_POR_OFFSET, timezoneFromLegacy(),
//  legacyFromTimezone() y LEGACY_POR_OFFSET_INVERSO: todo el puente hacia el
//  modelo viejo de índice + offset entero. `proyectos.timezone` viaja en el
//  token y en la caché de proyectos, así que no queda nada que derivar ni un
//  trío viejo que mantener escrito.)

/** Orden de los grupos en el selector. */
const ORDEN_GRUPOS = ['Base', 'Chile', 'América', 'Europa', 'Asia / Oceanía'];

/** Opciones agrupadas para un <select> con <optgroup>. */
/**
 * Texto largo para el `title` de cada opción: el id IANA y la explicación
 * completa, que en la etiqueta no cabe.
 */
export function detalleTimezone(zona) {
  const id = resolveTimezone(zona);
  return tieneHorarioDeVerano(id)
    ? `${id} — ahora ${formatoGmt(offsetHoras(id))}; ajusta solo el horario de verano`
    : `${id} — ${formatoGmt(offsetHoras(id))} todo el año, sin cambio de hora`;
}

export function timezoneOptionGroups() {
  return ORDEN_GRUPOS
    .map((label) => ({
      label,
      options: SUPPORTED_TIMEZONES
        .filter((z) => z.grupo === label)
        .map(({ id }) => ({ value: id, text: describirTimezone(id), detalle: detalleTimezone(id) }))
    }))
    .filter((g) => g.options.length > 0);
}

/**
 * Offset actual de un huso, en horas. Usa Intl para no romper la regla de que
 * este módulo es una HOJA (no importa nada de la app, ni siquiera dayjs).
 */
export function offsetHoras(zona, fecha = new Date()) {
  try {
    // formato-regional-ok: no es presentación. El locale se FIJA a propósito
    // para que la salida sea siempre 'GMT-04:00' y el parseo de abajo sea
    // determinista; con el locale del usuario el texto cambia y el regex falla.
    const fmt = new Intl.DateTimeFormat('en-US', { timeZone: zona, timeZoneName: 'longOffset' });
    const parte = fmt.formatToParts(fecha).find((p) => p.type === 'timeZoneName');
    const m = /GMT([+-])(\d{1,2}):?(\d{2})?/.exec(parte ? parte.value : '');
    if (!m) return 0; // 'GMT' a secas = UTC
    return (m[1] === '-' ? -1 : 1) * (Number(m[2]) + Number(m[3] || 0) / 60);
  } catch {
    return 0;
  }
}

/** Offset de un huso en este instante, en horas. */
export function offsetHorasActual(zona) {
  return offsetHoras(zona);
}

/** -4 -> 'GMT-4' ; 5.5 -> 'GMT+5:30' ; 0 -> 'GMT+0' */
export function formatoGmt(horas) {
  const signo = horas < 0 ? '-' : '+';
  const abs = Math.abs(horas);
  const h = Math.floor(abs);
  const min = Math.round((abs - h) * 60);
  return `GMT${signo}${h}${min ? ':' + String(min).padStart(2, '0') : ''}`;
}

/**
 * ¿Este huso cambia de hora en el año?
 *
 * Se compara el offset de enero contra el de julio. Cubre los dos hemisferios:
 * da igual en cuál caiga el verano, lo que importa es que los dos valores
 * difieran. Un huso fijo (Perú, Bogotá, Magallanes) da lo mismo en ambos.
 */
export function tieneHorarioDeVerano(zona) {
  const anio = new Date().getUTCFullYear();
  return offsetHoras(zona, new Date(Date.UTC(anio, 0, 15))) !== offsetHoras(zona, new Date(Date.UTC(anio, 6, 15)));
}

/**
 * Etiqueta para el selector: nombre + offset ACTUAL + si cambia en verano.
 *
 *   Chile continental (GMT-4, cambia)
 *   Perú (GMT-5, fijo)
 *
 * Se abrevia a propósito: el <select> mide ~340 px y 'cambia en verano' quedaba
 * cortado justo en la parte que importa. El texto completo va en el title.
 *
 * El offset se calcula al vuelo, no se guarda: en diciembre Chile dirá GMT-3
 * solo. Ese es justamente el punto de haber migrado a IANA, y conviene que se
 * vea en la pantalla donde alguien elige el huso.
 */
export function describirTimezone(zona) {
  const id = resolveTimezone(zona);
  const entrada = SUPPORTED_TIMEZONES.find((z) => z.id === id);
  const nombre = entrada ? entrada.label : id;
  const cambio = tieneHorarioDeVerano(id) ? ', cambia' : ', fijo';
  return `${nombre} (${formatoGmt(offsetHoras(id))}${cambio})`;
}

/** Offset nominal de cada índice viejo, para poder invertir el mapeo. */

export default {
  SUPPORTED_TIMEZONES,
  SUPPORTED_TIMEZONE_IDS,
  DEFAULT_TIMEZONE,
  isSupportedTimezone,
  resolveTimezone,
  timezoneOptions,
  timezoneOptionGroups,
  offsetHoras,
  offsetHorasActual,
  formatoGmt,
  tieneHorarioDeVerano,
  describirTimezone,
  detalleTimezone,
};
